import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BarcodeFormat,
  BrowserMultiFormatReader,
  DecodeHintType,
  NotFoundException
} from '@zxing/library'
import { useNavigate } from 'react-router-dom'
import Card from '../templates/ui/Card'
import Button from '../templates/ui/Button'
import CenterPopup from '../templates/ui/CenterPopup'
import './ScanReceipt.css'

const parseUrlSafely = (rawValue) => {
  if (!rawValue) return null
  try {
    return new URL(rawValue, window.location.origin)
  } catch {
    return null
  }
}

const decodeURIComponentSafely = (rawValue) => {
  if (!rawValue) return ''
  try {
    return decodeURIComponent(rawValue)
  } catch {
    return rawValue
  }
}

const normalizeRawText = (value) => String(value || '').replace(/\s+/g, '').trim()
const SCAN_TIMEOUT_MS = 12000

const resolveCameraErrorMessage = (error, fallbackMessage) => {
  const name = String(error?.name || '').toLowerCase()
  const message = String(error?.message || '').toLowerCase()

  if (name.includes('notallowed') || message.includes('permission')) {
    return 'ยังไม่ได้อนุญาตสิทธิ์กล้อง กรุณาอนุญาต Camera แล้วลองใหม่'
  }
  if (name.includes('notfound') || message.includes('requested device not found')) {
    return 'ไม่พบกล้องบนอุปกรณ์นี้'
  }
  if (name.includes('notreadable') || message.includes('could not start video source')) {
    return 'กล้องกำลังถูกใช้งานโดยแอปอื่น กรุณาปิดแอปกล้องอื่นก่อน'
  }
  if (name.includes('security') || message.includes('secure context')) {
    return 'เปิดกล้องได้เฉพาะลิงก์ HTTPS เท่านั้น'
  }
  if (name.includes('overconstrained')) {
    return 'การตั้งค่ากล้องไม่รองรับกับอุปกรณ์นี้'
  }
  if (name.includes('abort')) {
    return 'การเปิดกล้องถูกยกเลิก กรุณาลองใหม่'
  }
  if (name.includes('timeout') || message.includes('timeout')) {
    return 'หมดเวลาการเชื่อมต่อกล้อง กรุณาลองใหม่'
  }

  return fallbackMessage || 'เปิดกล้องไม่สำเร็จ'
}

const extractMuteluQueryFromText = (rawValue) => {
  const text = normalizeRawText(rawValue)
  if (!text) return null

  const collect = (key) => {
    const regex = new RegExp(`[?&]${key}=([^&#]+)`, 'i')
    const match = text.match(regex)
    return match?.[1] ? decodeURIComponentSafely(match[1]) : ''
  }

  const receiptNo = collect('receiptNo') || collect('receipt_no') || collect('receipt')
  const partnerCode =
    collect('partnerCode') || collect('partner_code') || collect('partnerId') || collect('partner_id')
  if (!receiptNo || !partnerCode) return null

  const customer =
    collect('customer') || collect('customerId') || collect('customer_id') || collect('cus_id')
  const element = collect('element') || collect('elementCode') || collect('element_code')
  const trnNo = collect('trnNo') || collect('trn_no') || collect('transactionNo')

  const params = new URLSearchParams()
  params.set('receiptNo', receiptNo)
  params.set('partnerId', partnerCode)
  if (customer) params.set('customer', customer)
  if (element) params.set('element', element)
  if (trnNo) params.set('trnNo', trnNo)
  return params.toString()
}

export default function ScanReceipt() {
  const navigate = useNavigate()
  const videoRef = useRef(null)
  const readerRef = useRef(null)
  const nativeDetectorRef = useRef(null)
  const nativeDetectTimerRef = useRef(null)
  const scanTimeoutRef = useRef(null)
  const scannedRef = useRef(false)
  const isStartingRef = useRef(false)
  const [isStarting, setIsStarting] = useState(false)
  const [scanStatus, setScanStatus] = useState('กำลังเตรียมกล้อง')
  const [scanError, setScanError] = useState('')
  const [scanErrorDetail, setScanErrorDetail] = useState('')
  const [invalidQrPopupOpen, setInvalidQrPopupOpen] = useState(false)

  const clearScanTimeout = useCallback(() => {
    if (scanTimeoutRef.current) {
      window.clearTimeout(scanTimeoutRef.current)
      scanTimeoutRef.current = null
    }
  }, [])

  const startScanTimeout = useCallback(() => {
    clearScanTimeout()
    scanTimeoutRef.current = window.setTimeout(() => {
      if (scannedRef.current) return
      setScanError('ยังไม่พบ QR ในกรอบสแกน')
      setScanErrorDetail('ขยับให้ QR ชัดขึ้น เพิ่มแสง และให้ QR อยู่เต็มกรอบเส้นประ')
      setScanStatus('กล้องพร้อมสแกน')
    }, SCAN_TIMEOUT_MS)
  }, [clearScanTimeout])

  const setErrorFromException = useCallback((error, fallbackMessage) => {
    const errorMessage = resolveCameraErrorMessage(error, fallbackMessage)
    const name = String(error?.name || 'UnknownError')
    const detail = String(error?.message || '')
    setScanError(errorMessage)
    setScanErrorDetail(detail ? `${name}: ${detail}` : name)
    console.error('[Mutelu][ScanReceipt] scan error', {
      name,
      message: detail,
      fallbackMessage
    })
  }, [])

  const stopScanner = useCallback(() => {
    if (readerRef.current) {
      readerRef.current.reset()
      readerRef.current = null
    }
    nativeDetectorRef.current = null
    if (nativeDetectTimerRef.current) {
      window.clearTimeout(nativeDetectTimerRef.current)
      nativeDetectTimerRef.current = null
    }
    clearScanTimeout()
    const videoElement = videoRef.current
    const stream = videoElement?.srcObject
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((track) => track.stop())
      videoElement.srcObject = null
    }
    isStartingRef.current = false
    setIsStarting(false)
    setScanStatus('กล้องพร้อมสแกน')
  }, [clearScanTimeout])

  const startNativeBarcodeDetectLoop = useCallback(() => {
    if (typeof window === 'undefined') return
    if (!videoRef.current) return
    if (scannedRef.current) return
    if (nativeDetectorRef.current) return
    if (typeof window.BarcodeDetector !== 'function') return

    try {
      nativeDetectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] })
    } catch {
      nativeDetectorRef.current = null
      return
    }

    const detectOnce = async () => {
      if (scannedRef.current || !videoRef.current || !nativeDetectorRef.current) return
      try {
        const barcodes = await nativeDetectorRef.current.detect(videoRef.current)
        const first = Array.isArray(barcodes) ? barcodes[0] : null
        const text = String(first?.rawValue || first?.displayValue || '').trim()
        if (text) {
          scannedRef.current = true
          const extractedQuery = extractMuteluQueryFromText(text)
          if (extractedQuery) {
            try {
              navigate(`/game/mutelu/receipt?${extractedQuery}`, { replace: true })
            } finally {
              stopScanner()
            }
          } else {
            try {
              setInvalidQrPopupOpen(true)
              setScanStatus('QR ไม่ถูกต้อง กรุณาสแกนใหม่')
            } finally {
              stopScanner()
            }
          }
          return
        }
      } catch (error) {
        console.warn('[Mutelu][ScanReceipt] native barcode detect failed', error)
      }
      nativeDetectTimerRef.current = window.setTimeout(detectOnce, 220)
    }

    nativeDetectTimerRef.current = window.setTimeout(detectOnce, 220)
  }, [navigate, stopScanner])

  const goToSubmit = useCallback((rawValue) => {
    const value = String(rawValue || '').trim()
    if (!value) return false
    const extractedQuery = extractMuteluQueryFromText(value)
    if (extractedQuery) {
      navigate(`/game/mutelu/receipt?${extractedQuery}`, { replace: true })
      return true
    }
    const candidates = [value, decodeURIComponentSafely(value)]
    for (const candidate of candidates) {
      const parsed = parseUrlSafely(candidate)
      if (!parsed) continue

      const directIsReceiptPath = /\/game\/mutelu\/receipt\/?$/.test(parsed.pathname)
      const directQuery = parsed.searchParams.toString()
      if (directIsReceiptPath && directQuery.length > 0) {
        navigate(`/game/mutelu/receipt?${directQuery}`, { replace: true })
        return true
      }

      const liffStateRaw = parsed.searchParams.get('liff.state')
      if (liffStateRaw) {
        const liffStateDecoded = decodeURIComponentSafely(liffStateRaw)
        const liffParsed = parseUrlSafely(liffStateDecoded)
        const liffQuery = liffParsed?.searchParams?.toString() || ''
        const liffPath = liffParsed?.pathname || ''
        if (/\/game\/mutelu\/receipt\/?$/.test(liffPath) && liffQuery.length > 0) {
          navigate(`/game/mutelu/receipt?${liffQuery}`, { replace: true })
          return true
        }
      }
    }
    return false
  }, [navigate])

  const handleScanSuccess = useCallback((value) => {
    if (scannedRef.current) return
    scannedRef.current = true
    let accepted = false
    try {
      accepted = goToSubmit(value)
      if (accepted) {
        setScanStatus('อ่าน QR สำเร็จ กำลังไปหน้าถัดไป...')
      } else {
        setScanStatus('QR ไม่ถูกต้อง กรุณาสแกนใหม่')
        setInvalidQrPopupOpen(true)
      }
    } finally {
      try {
        stopScanner()
      } catch {}
    }
  }, [goToSubmit, stopScanner])

  const startScanner = useCallback(async () => {
    if (isStartingRef.current) return
    if (!videoRef.current) return
    if (readerRef.current) {
      stopScanner()
    }
    isStartingRef.current = true
    setIsStarting(true)
    setScanStatus('กำลังเปิดกล้อง...')
    setScanError('')
    setScanErrorDetail('')
    scannedRef.current = false

    const hints = new Map()
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE])
    hints.set(DecodeHintType.TRY_HARDER, true)
    hints.set(DecodeHintType.ALSO_INVERTED, true)
    const codeReader = new BrowserMultiFormatReader(hints, 200)
    readerRef.current = codeReader

    const onDecode = (result, error) => {
      if (result) {
        const text =
          typeof result.getText === 'function' ? result.getText() : result.text || ''
        if (text) handleScanSuccess(text)
      }

      if (error && !(error instanceof NotFoundException)) {
        setErrorFromException(error, 'ระบบอ่าน QR ไม่สำเร็จ')
      }
    }

    const startDecodeWithConstraint = async () => {
      if (videoRef.current && typeof videoRef.current.setAttribute === 'function') {
        videoRef.current.setAttribute('playsinline', 'true')
        videoRef.current.setAttribute('autoplay', 'true')
        videoRef.current.setAttribute('muted', 'true')
      }
      startNativeBarcodeDetectLoop()
      startScanTimeout()
      setScanStatus('กล้องพร้อมสแกน')
      return codeReader.decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' }]
          }
        },
        videoRef.current,
        onDecode
      )
    }

    const startDecodeWithDevice = async () => {
      const devices = await codeReader.listVideoInputDevices()
      const preferredDevice =
        devices.find((device) => /(back|rear|environment)/i.test(device.label || '')) ||
        devices[devices.length - 1] ||
        devices[0]
      startNativeBarcodeDetectLoop()
      startScanTimeout()
      setScanStatus('กล้องพร้อมสแกน')
      return codeReader.decodeFromVideoDevice(preferredDevice?.deviceId, videoRef.current, onDecode)
    }

    ;(async () => {
      try {
        try {
          await startDecodeWithConstraint()
        } catch (constraintError) {
          console.warn('[Mutelu][ScanReceipt] decodeFromConstraints failed -> fallback to device', constraintError)
          await startDecodeWithDevice()
        }
      } catch (error) {
        setErrorFromException(error, 'ไม่สามารถเปิดกล้องได้ กรุณาอนุญาตสิทธิ์กล้อง')
      }
    })()

    isStartingRef.current = false
    setIsStarting(false)
  }, [handleScanSuccess, setErrorFromException, startNativeBarcodeDetectLoop, startScanTimeout, stopScanner])

  const handleRestartScan = useCallback(() => {
    stopScanner()
    startScanner()
  }, [startScanner, stopScanner])

  const invalidQrPopupTemplate = {
    icon: '⚠️',
    title: 'QR Code ไม่ถูกต้อง',
    subtitle: 'ไม่ใช่ QR สำหรับเกมมูเตลู',
    message: 'กรุณาสแกน QR จากใบเสร็จ Big C ที่ใช้ร่วมรายการเท่านั้น',
    confirmLabel: 'สแกนใหม่',
    cancelLabel: ''
  }

  const handleCloseInvalidQrPopup = useCallback(() => {
    setInvalidQrPopupOpen(false)
    handleRestartScan()
  }, [handleRestartScan])

  useEffect(() => {
    startScanner()
    return () => {
      stopScanner()
    }
  }, [startScanner, stopScanner])

  return (
    <section className="mobile-page scan-page">
      <Card className="mobile-panel scan-card">
        <div className="scan-header">
          <p className="scan-badge">Step 1</p>
          <h1 className="scan-title">สแกน QR ใบเสร็จ</h1>
          <p className="scan-subtitle">วาง QR ให้อยู่กลางกรอบ แล้วรอระบบตรวจจับอัตโนมัติ</p>
        </div>

        <div className="scan-video-shell">
          <video ref={videoRef} className="scan-video" muted playsInline autoPlay />
          <div className="scan-frame" aria-hidden="true" />
        </div>

        <p className="scan-status">{scanStatus}</p>
        {scanError ? <p className="scan-error">{scanError}</p> : null}
        {scanErrorDetail ? <p className="scan-error-detail">{scanErrorDetail}</p> : null}

        <div className="scan-actions">
          <Button
            type="button"
            className="scan-primary-btn btn-uniform"
            onClick={handleRestartScan}
            disabled={isStarting}
          >
            {isStarting ? 'กำลังเปิดกล้อง...' : 'สแกนอีกครั้ง'}
          </Button>
        </div>
      </Card>
      <CenterPopup
        open={invalidQrPopupOpen}
        template={invalidQrPopupTemplate}
        onConfirm={handleCloseInvalidQrPopup}
        onCancel={handleCloseInvalidQrPopup}
      />
    </section>
  )
}
